import { useEffect, useState, useRef, useCallback } from "react";
import "./AIDebugger.css";
import { RefreshCcw, X, Copy, FileJson, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { setWorkspaceUsers } from "../../redux/features/agent/agentSlice";
import { selectUser } from "../../redux/features/appData";
import { useSelector } from "react-redux";
import { TracingEvent } from "winslow-core";
import { apiGetTracingsForWorkspace, apiDeleteAllTracingsForWorkspace } from "../../services/AIService";
import { GenericSelect, GenericSelectOption } from "../shared/GenericSelect";
import { Icon } from "../../ds2/components";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../config";
import Modal from "../shared/Modal";

const STORAGE_KEY = "ai-debugger-width";
const DEFAULT_WIDTH = 30; 
const MIN_WIDTH = 15; // percentage
const MAX_WIDTH = 70; // percentage

type AIDebuggerProps = {
  isOpen: boolean;
  onClose: () => void;
  lastMessage: number;
  messages?: string[];
};

// don't close it, just hide it so the data persists

export const AIDebugger = ({ isOpen, onClose, lastMessage, messages }: AIDebuggerProps) => {

  const storeUser = useSelector(selectUser);

  const [savedLastMessage, setSavedLastMessage] = useState<number>(0);
  const [tracings, setTracings] = useState<TracingEvent[] | null>(null);
  const [activeTab, setActiveTab] = useState<"messages" | "tracings">("messages");
  const [width, setWidth] = useState<number>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? parseFloat(saved) : DEFAULT_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isAutoReloadEnabled, setIsAutoReloadEnabled] = useState(false);
  const [expandedTraces, setExpandedTraces] = useState<Set<string>>(new Set());
  const debuggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (lastMessage > savedLastMessage) {
      setSavedLastMessage(lastMessage);

      (async () => {
        await loadTracings();
      })();
    }
  }, [lastMessage, savedLastMessage]);

  useEffect(() => {
    if (lastMessage > savedLastMessage) {
      setSavedLastMessage(lastMessage);
    }
  }, [lastMessage, savedLastMessage]);

  // Save width to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, width.toString());
  }, [width]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || !debuggerRef.current) return;

      const container = debuggerRef.current.parentElement;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const newWidth = ((containerRect.right - e.clientX) / containerRect.width) * 100;
      
      // Clamp width between MIN_WIDTH and MAX_WIDTH
      const clampedWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));
      setWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing]);

  const loadTracings = useCallback(async () => {
    if (!storeUser?.currentWorkspaceId) return;
    const response = await apiGetTracingsForWorkspace(storeUser.currentWorkspaceId);
    
    // Sort tracings by timestamp (oldest first)
    if (response) {
      response.sort((a, b) => {
        const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return timeA - timeB; // Ascending order (oldest first)
      });
    }
    
    setTracings(response);
  }, [storeUser?.currentWorkspaceId]);

  const handleRefreshToggle = async () => {
    const newState = !isAutoReloadEnabled;
    setIsAutoReloadEnabled(newState);
    setIsDropdownOpen(false); // Reset dropdown state to allow auto-reloading
    
    if (newState) {
      // If turning on, reload immediately
      await loadTracings();
    }
  }

  const handleDeleteAllTracings = async () => {
    if (!storeUser?.currentWorkspaceId) {
      toast("No workspace selected", toastFail);
      return;
    }

    if (!window.confirm("Are you sure you want to delete all tracings? This action cannot be undone.")) {
      return;
    }

    try {
      const result = await apiDeleteAllTracingsForWorkspace(storeUser.currentWorkspaceId);
      if (result) {
        toast(`Deleted ${result.count} tracing(s)`, toastSuccess);
        setTracings([]);
        setIsAutoReloadEnabled(false); // Turn off auto-reload after deletion
      } else {
        toast("Failed to delete tracings", toastFail);
      }
    } catch (err) {
      console.error("Failed to delete tracings:", err);
      toast("Failed to delete tracings", toastFail);
    }
  }

  // Auto-reload tracings every 500ms when tracings tab is active and toggle is on
  // Pause reloading when a dropdown is open
  useEffect(() => {
    if (!isOpen || activeTab !== "tracings" || !isAutoReloadEnabled || isDropdownOpen) {
      return;
    }

    // Load immediately when auto-reload is enabled
    loadTracings();

    // Set up interval to reload every 500ms
    const interval = setInterval(() => {
      loadTracings();
    }, 500);

    return () => {
      clearInterval(interval);
    };
  }, [isOpen, activeTab, isAutoReloadEnabled, isDropdownOpen, loadTracings]);

  const formatJSON = (value: any): string => {
    if (!value) return "";
    
    try {
      // If it's already a string, try to parse it first
      if (typeof value === "string") {
        const parsed = JSON.parse(value);
        return JSON.stringify(parsed, null, 2);
      }
      // If it's an object, stringify it with formatting
      return JSON.stringify(value, null, 2);
    } catch (e) {
      // If parsing fails, just return the original value as string
      return String(value);
    }
  }

  // Parse tags from prompt (format: <TAG>...</TAG>, <TAG>..., or LABEL:)
  const parseTags = (prompt: string): Array<{ name: string; fullTag: string; innerContent: string }> => {
    if (!prompt) return [];
    const tags: Array<{ name: string; fullTag: string; innerContent: string }> = [];
    const processedIndices = new Set<number>();
    
    // First, match complete XML-style tags like <TAG>content</TAG> (supports underscores)
    const completeTagRegex = /<([A-Za-z_]+)>([\s\S]*?)<\/\1>/g;
    let match;
    
    while ((match = completeTagRegex.exec(prompt)) !== null) {
      const tagName = match[1];
      const fullTag = match[0];
      const innerContent = match[2];
      tags.push({ name: tagName, fullTag, innerContent });
      // Mark this range as processed
      for (let i = match.index; i < match.index + fullTag.length; i++) {
        processedIndices.add(i);
      }
    }
    
    // Then, match incomplete tags (opening tag without closing tag) - only if not already processed
    const incompleteTagRegex = /<([A-Za-z_]+)>([\s\S]*?)(?=<[A-Za-z_/]|$)/g;
    let incompleteMatch;
    
    while ((incompleteMatch = incompleteTagRegex.exec(prompt)) !== null) {
      const startPos = incompleteMatch.index;
      // Check if this position was already processed as part of a complete tag
      if (!processedIndices.has(startPos)) {
        const tagName = incompleteMatch[1];
        const fullTag = incompleteMatch[0];
        const innerContent = incompleteMatch[2];
        tags.push({ name: tagName, fullTag, innerContent });
      }
    }
    
    // Also parse standalone labels like "SYSTEM:" or "RULES:" that appear at the start of lines
    // Only add if we haven't already found this as a tag
    const labelRegex = /^(SYSTEM|RULES|OUTPUT_FORMAT|MESSAGE_HISTORY|NEW_MESSAGE):\s*([\s\S]*?)(?=^(?:SYSTEM|RULES|OUTPUT_FORMAT|MESSAGE_HISTORY|NEW_MESSAGE):|$)/gim;
    let labelMatch;
    
    while ((labelMatch = labelRegex.exec(prompt)) !== null) {
      const labelName = labelMatch[1];
      const labelContent = labelMatch[2].trim();
      
      if (!tags.some(tag => tag.name === labelName)) {
        tags.push({ 
          name: labelName, 
          fullTag: labelMatch[0], 
          innerContent: labelContent 
        });
      }
    }
    
    return tags;
  }

  const TracingItem = ({ tracing, onDropdownChange, isExpanded, onToggleExpand }: { tracing: TracingEvent; onDropdownChange: (isOpen: boolean) => void; isExpanded: boolean; onToggleExpand: () => void }) => {
    const tags = parseTags(tracing.inputPrompt || "");
    const DEFAULT_VALUE = -1; // Special value for default option
    
    const tagOptions: GenericSelectOption[] = [
      {
        displayName: tags.length > 0 ? "(pick a block)" : "(no blocks)",
        value: DEFAULT_VALUE,
      },
      ...tags.map((tag, index) => ({
        displayName: tag.name,
        value: index,
      })),
    ];

    const [selectedTagIndex, setSelectedTagIndex] = useState<number>(DEFAULT_VALUE);
    const [showJsonModal, setShowJsonModal] = useState(false);
    const [jsonContent, setJsonContent] = useState<string>("");
    const [selectedTagName, setSelectedTagName] = useState<string>("");

    const selectedTagOption = tagOptions.find(opt => opt.value === selectedTagIndex);

    const handleCopyTag = async () => {
      try {
        // If no block is selected, copy the whole prompt
        if (selectedTagIndex === DEFAULT_VALUE || selectedTagIndex === null || !tags[selectedTagIndex]) {
          await navigator.clipboard.writeText(tracing.inputPrompt || "");
          toast("Copied complete prompt", toastSuccess);
          return;
        }
        
        // Otherwise, copy the selected tag
        await navigator.clipboard.writeText(tags[selectedTagIndex].fullTag);
        const tagName = tags[selectedTagIndex].name;
        toast(`<${tagName}> copied`, toastSuccess);
      } catch (err) {
        console.error("Failed to copy:", err);
        toast("Failed to copy", toastFail);
      }
    };

    const handleViewJson = () => {
      if (selectedTagIndex === DEFAULT_VALUE || selectedTagIndex === null || !tags[selectedTagIndex]) return;
      
      try {
        // Get the inner content directly from the parsed tag
        const innerContent = tags[selectedTagIndex].innerContent.trim();
        const tagName = tags[selectedTagIndex].name;
        
        if (!innerContent) {
          toast("Tag is empty", toastFail);
          return;
        }
        
        // Store the tag name for display in modal
        setSelectedTagName(tagName);
        
        // Try to parse as JSON and format it
        try {
          const parsed = JSON.parse(innerContent);
          setJsonContent(JSON.stringify(parsed, null, 2));
        } catch (e) {
          // If it's not valid JSON, show the raw content
          setJsonContent(innerContent);
        }
        
        setShowJsonModal(true);
      } catch (err) {
        console.error("Failed to extract tag content:", err);
        toast("Failed to extract tag content", toastFail);
      }
    };

    return (
      <>
        <div 
          key={tracing.id} 
          className="ai-debugger-tracing-header"
          onClick={onToggleExpand}
          style={{ cursor: "pointer" }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
            <div style={{ marginTop: "2px" }}>
              {isExpanded ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div className="ai-debugger-tracing-item">
                ----------Trace:-------<br/>
                {tracing.id}
              </div>
              
              {(tracing as any).description && (
                <div className="ai-debugger-tracing-description">
                  <strong>Description:</strong> {(tracing as any).description}
                </div>
              )}
              
              {tracing.model && (
                <div className="ai-debugger-tracing-model">
                  Model: {tracing.model}
                </div>
              )}
              
              {tracing.inputMessage && (
                <div className="ai-debugger-tracing-input-preview">
                  User Prompt: {tracing.inputMessage}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {isExpanded && (
          <>
            <div className="ai-debugger-tags-section">
            <div className="ai-debugger-tags-controls">
              <div 
                className="ai-debugger-tags-dropdown-wrapper"
                onMouseDown={()=> {setIsAutoReloadEnabled(false)}}
              >
                <GenericSelect
                  key={"gs-" + tracing.id}
                  selected={selectedTagOption}
                  options={tagOptions}
                  onChange={(value) => setSelectedTagIndex(value !== null ? value : null)}
                />
              </div>
              <Icon
                icon={<Copy size={16} />}
                variant="primary"
                size="sm"
                onClick={handleCopyTag}
              />
              <Icon
                icon={<FileJson size={16} />}
                variant="primary"
                size="sm"
                onClick={handleViewJson}
                disabled={selectedTagIndex === DEFAULT_VALUE || selectedTagIndex === null || tags.length === 0}
              />
            </div>
          </div>

        <div 
          style={{backgroundColor: "white", padding: "4px", border: "var(--card-border)"}}
          className="ai-debugger-tracing-item-content"
        >
          <pre style={{ fontSize: "0.75rem" , whiteSpace: "pre-wrap"}}>
           {tracing.inputPrompt}
          </pre>
        </div>
        <div 
          style={{backgroundColor: "#FEFEFE", padding: "4px", border: "var(--card-border)"}}
          className="ai-debugger-tracing-item-content"
        >
          <pre style={{ fontSize: "0.75rem", whiteSpace: "pre-wrap" }}>{formatJSON(tracing.outputResponse)}</pre>
        </div>
        <div 
          style={{backgroundColor: "#FEFEFE", padding: "4px", border: "var(--card-border)"}}
          className="ai-debugger-tracing-item-content"
        >
          <pre style={{ fontSize: "0.75rem", whiteSpace: "pre-wrap" }}>{tracing.errorMessage}</pre>
        </div>
          </>
        )}

        <Modal
          isOpen={showJsonModal}
          onClose={() => setShowJsonModal(false)}
          style={{ 
            width: "100vw", 
            height: "100vh", 
            maxWidth: "100vw",
            maxHeight: "100vh",
            margin: 0,
            borderRadius: 0
          }}
          canClose={true}
          escapeToClose={true}
          allowClickout={true}
        >
          <div className="ai-debugger-json-modal">
            <div className="ai-debugger-json-modal-header">
              <h2>&lt;{selectedTagName}&gt; Content (JSON)</h2>
            </div>
            <div className="ai-debugger-json-modal-content">
              <pre className="ai-debugger-json-modal-pre">
                {jsonContent}
              </pre>
            </div>
          </div>
        </Modal>
      </>
    );
  };
  
  return (
    <div 
      ref={debuggerRef}
      className="ai-debugger" 
      style={{ 
        display: isOpen ? "flex" : "none",
        width: `${width}%`
      }}>
      <div 
        className="ai-debugger-resize-handle"
        onMouseDown={handleMouseDown}
      />
      <div className="ai-debugger-inner">
      <div className="ai-debugger-header">
        <span style={{ fontSize: "16px", fontWeight: "600" }}>Debugger</span>
        <div className="ai-debugger-close">
          <div
            onClick={handleRefreshToggle}
            style={{ 
              cursor: "pointer",
              backgroundColor: isAutoReloadEnabled ? "#000" : "transparent",
              padding: "4px",
              borderRadius: "4px",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <RefreshCcw
              size={16}
              color={isAutoReloadEnabled ? "#fff" : undefined}
            />
          </div>
          <div
            onClick={handleDeleteAllTracings}
            style={{ 
              cursor: "pointer",
              padding: "4px",
              borderRadius: "4px",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
            title="Delete all tracings"
          >
            <Trash2 size={16} />
          </div>
          <X 
            size={16} 
            onClick={onClose}
            style={{ cursor: "pointer" }}
          />
        </div>
      </div>

      <div className="ai-debugger-tabs">
        <button
          className={`ai-debugger-tab ${activeTab === "messages" ? "ai-debugger-tab-active" : ""}`}
          onClick={() => setActiveTab("messages")}
        >
          Messages {messages && messages.length > 0 && `(${messages.length})`}
        </button>
        <button
          className={`ai-debugger-tab ${activeTab === "tracings" ? "ai-debugger-tab-active" : ""}`}
          onClick={() => setActiveTab("tracings")}
        >
          Tracings {tracings && tracings.length > 0 && `(${tracings.length})`}
        </button>
      </div>

      <div className="ai-debugger-content">
        {activeTab === "messages" && (
          <div className="ai-debugger-messages">
            {messages && messages.length > 0 ? (
              messages.map((message, index) => (
                <div key={"message" + index} className="ai-debugger-message-item">
                  {message}
                </div>
              ))
            ) : (
              <div className="ai-debugger-empty">No debug messages</div>
            )}
          </div>
        )}

        {activeTab === "tracings" && (
          <div className="ai-debugger-tracings">
            {tracings && tracings.length > 0 ? (
              tracings.map((tracing) => {
                const isExpanded = expandedTraces.has(tracing.id);
                const toggleExpand = () => {
                  setExpandedTraces(prev => {
                    const newSet = new Set(prev);
                    if (newSet.has(tracing.id)) {
                      newSet.delete(tracing.id);
                    } else {
                      newSet.add(tracing.id);
                    }
                    return newSet;
                  });
                };
                return (
                  <TracingItem 
                    key={tracing.id} 
                    tracing={tracing} 
                    onDropdownChange={setIsDropdownOpen}
                    isExpanded={isExpanded}
                    onToggleExpand={toggleExpand}
                  />
                );
              })
            ) : (
              <div className="ai-debugger-empty">No tracings available</div>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
  );
};
